import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useDispatch} from 'react-redux'

import type {
  AllocationEditResult,
  AllocationPrefillAnswer,
  AllocationPrefillResult,
  AllocationProgress,
  AllocationRemovalRequest,
  AllocationServerError,
  AllocationServerGrid,
} from '../types/allocationServer'
import {
  buildServerGridView,
  mergeServerCells,
  withCellTeacher,
} from '../utils/allocationServerAdapter'
import {
  type AllocationScopeQuery,
  allocationServerApi as api,
  isAllocationUnavailable,
} from '../utils/allocationServerApi'

/** How often a file the Teachers step is still reading is asked about again (handoff §2.1). */
const PREFILL_POLL_MS = 3000

export type AllocationServerStatus = 'loading' | 'ready' | 'unavailable' | 'error'

type Params = {
  /** The routes answered a bare 404: this server has no server-side allocation. */
  onUnavailable: () => void
  /** The uploaded file filled cells now (`batchId` undoes them). */
  /**
   * The uploaded file's lines were placed (`batchId` to undo them). A re-upload also says how
   * many cells already had their teacher and how many another teacher keeps (AI server).
   */
  onPrefillApplied?: (result: {
    filled: number
    batchId: string
    alreadyAssigned: number
    heldByOther: number
  }) => void
  /** Any saved change (cell edit, removal, undo, applied proposal). */
  onEdited?: () => void
  /** A refused or failed request the user should hear about. */
  onError?: (error: AllocationServerError) => void
  /** The previewed proposal is no longer pending (applied or discarded elsewhere). */
  onPreviewGone?: () => void
}

type Progress = {overall: AllocationProgress | null; canContinue: boolean; prefilledCount: number}

/** Another tab saved at the same moment: retried once (handoff §1.1, WORKSPACE_BUSY). */
const retryBusy = <T>(run: () => Promise<T>): Promise<T> =>
  run().catch((error: AllocationServerError) =>
    error?.code === 'WORKSPACE_BUSY' ? run() : Promise.reject(error)
  )

/**
 * Server-side Teacher Allocation (handoff §2–§5): the server holds the grid, every edit is saved
 * when it is made, and the progress numbers come back with each answer. The portal keeps only
 * the last grid it fetched (filter `all`; Assigned / Unassigned are applied on screen the same
 * way the server does) and merges each edit's cells into it.
 */
export function useAllocationServer({
  onUnavailable,
  onPrefillApplied,
  onEdited,
  onError,
  onPreviewGone,
}: Params) {
  const dispatch = useDispatch()
  const [status, setStatus] = useState<AllocationServerStatus>('loading')
  const [progress, setProgress] = useState<Progress>({
    overall: null,
    canContinue: false,
    prefilledCount: 0,
  })
  const [prefill, setPrefill] = useState<AllocationPrefillResult | null>(null)
  const [answeringPrefill, setAnsweringPrefill] = useState(false)
  const [grid, setGrid] = useState<AllocationServerGrid | null>(null)
  /**
   * The grid request in flight: `scope` when the rows will change (first load, a new Branch /
   * Level, entering or leaving a proposal preview), shown as a skeleton; `refresh` when the same
   * view is read again after a change, which keeps the table on screen.
   */
  const [gridLoading, setGridLoading] = useState<'scope' | 'refresh' | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  /** The grid shows a Versi proposal (§6.5): its id and the changes outside this view. */
  const [preview, setPreview] = useState<{proposalId: string; outsideView: number[]} | null>(null)
  const previewIdRef = useRef<string | null>(null)

  const mountedRef = useRef(true)
  const gridRef = useRef(grid)
  gridRef.current = grid
  const gridRequestRef = useRef(0)
  /** The Branch / Level the grid was last asked for (the server may resolve it differently). */
  const gridScopeRef = useRef<AllocationScopeQuery>({})
  const pollTimerRef = useRef<number | null>(null)
  const callbacksRef = useRef({onUnavailable, onPrefillApplied, onEdited, onError, onPreviewGone})
  callbacksRef.current = {onUnavailable, onPrefillApplied, onEdited, onError, onPreviewGone}

  const fail = useCallback((error: AllocationServerError) => {
    if (!mountedRef.current) return
    if (isAllocationUnavailable(error)) {
      setStatus('unavailable')
      callbacksRef.current.onUnavailable()
      return
    }
    callbacksRef.current.onError?.(error)
  }, [])

  const refreshSummary = useCallback(() => {
    setSummaryLoading(true)
    return api
      .summary(dispatch)
      .then(
        (res) => {
          if (!mountedRef.current) return
          setProgress({
            overall: res.overall,
            canContinue: res.canContinue,
            prefilledCount: res.prefilledCount,
          })
          setStatus('ready')
        },
        (error: AllocationServerError) => {
          if (!mountedRef.current) return
          if (isAllocationUnavailable(error)) {
            fail(error)
            return
          }
          setStatus((prev) => (prev === 'ready' ? prev : 'error'))
        }
      )
      .finally(() => {
        if (mountedRef.current) setSummaryLoading(false)
      })
  }, [dispatch, fail])

  /**
   * The grid for a Branch / Level (filter `all`). `proposalId` loads Versi's proposal preview
   * instead (§6.5) and stays until `null` is passed; a Branch / Level change keeps it.
   */
  const loadGrid = useCallback(
    (
      scope: AllocationScopeQuery = gridScopeRef.current,
      proposalId: string | null = previewIdRef.current
    ): Promise<void> => {
      const shown = gridRef.current
      const sameView =
        Boolean(shown) &&
        (scope.branchId ?? null) === (shown?.scope.branchId ?? null) &&
        (scope.levelId ?? null) === (shown?.scope.levelId ?? null) &&
        proposalId === previewIdRef.current
      gridScopeRef.current = scope
      previewIdRef.current = proposalId
      if (!proposalId) setPreview(null)
      const request = (gridRequestRef.current += 1)
      setGridLoading(sameView ? 'refresh' : 'scope')
      const load = proposalId
        ? api.proposalGrid(dispatch, proposalId, scope).then((res) => {
            if (mountedRef.current && request === gridRequestRef.current) {
              setPreview({proposalId, outsideView: res.preview?.outsideView ?? []})
            }
            return res
          })
        : api.grid(dispatch, scope, 'all')
      return load
        .then((res) => {
          if (!mountedRef.current || request !== gridRequestRef.current) return
          setGrid(res)
          gridScopeRef.current = {branchId: res.scope.branchId, levelId: res.scope.levelId}
          setProgress({
            overall: res.overall,
            canContinue: res.canContinue,
            prefilledCount: res.prefilledCount,
          })
          setStatus('ready')
        })
        .catch((error: AllocationServerError) => {
          if (proposalId && error?.code && error.code.startsWith('PROPOSAL_')) {
            // Applied or discarded elsewhere: back to the saved grid.
            previewIdRef.current = null
            setPreview(null)
            callbacksRef.current.onPreviewGone?.()
            return loadGrid(scope, null)
          }
          fail(error)
        })
        .finally(() => {
          if (mountedRef.current && request === gridRequestRef.current) setGridLoading(null)
        })
    },
    [dispatch, fail]
  )

  /** After a change the server made outside the grid (prefill, undo, removal, proposal). */
  const refresh = useCallback(() => {
    refreshSummary()
    if (gridRef.current) loadGrid()
  }, [loadGrid, refreshSummary])

  const takePrefill = useCallback(
    (res: AllocationPrefillResult) => {
      if (!mountedRef.current) return
      setPrefill(res)
      setProgress({
        overall: res.overall,
        canContinue: res.canContinue,
        prefilledCount: res.prefilledCount,
      })
      if (res.batchId && (res.filled ?? 0) > 0) {
        callbacksRef.current.onPrefillApplied?.({
          filled: res.filled ?? 0,
          batchId: res.batchId,
          alreadyAssigned: res.alreadyAssigned ?? 0,
          heldByOther: res.heldByOther?.length ?? 0,
        })
        if (gridRef.current) loadGrid()
      }
    },
    [loadGrid]
  )

  const runPrefill = useCallback(
    (branchId?: number | 'all'): Promise<void> =>
      api.prefill(dispatch, branchId).then(
        (res) => {
          takePrefill(res)
          if (res.state === 'still_reading' && mountedRef.current) {
            pollTimerRef.current = window.setTimeout(() => {
              pollTimerRef.current = null
              runPrefill().catch(() => undefined)
            }, PREFILL_POLL_MS)
          }
        },
        (error: AllocationServerError) => {
          // The prefill is a convenience: a refusal leaves the grid as it is.
          if (isAllocationUnavailable(error)) return Promise.reject(error)
          return undefined
        }
      ),
    [dispatch, takePrefill]
  )

  // On entry: place the uploaded file's lines, then read the progress (handoff §2.1, §2.2).
  useEffect(() => {
    mountedRef.current = true
    runPrefill()
      .then(() => refreshSummary())
      .catch(fail)
    return () => {
      mountedRef.current = false
      if (pollTimerRef.current !== null) window.clearTimeout(pollTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per visit
  }, [])

  /**
   * The prefill's unclear lines answered (`questions`: which classes a teacher's subject is in).
   * The answered lines are placed like the rest, and the answer reports what was filled.
   */
  const answerPrefillQuestions = useCallback(
    (answers: AllocationPrefillAnswer[]) => {
      if (answeringPrefill) return
      setAnsweringPrefill(true)
      api
        .answerPrefill(dispatch, answers)
        .then((res) => {
          takePrefill(res)
          return refreshSummary()
        })
        .catch(fail)
        .finally(() => {
          if (mountedRef.current) setAnsweringPrefill(false)
        })
    },
    [answeringPrefill, dispatch, fail, refreshSummary, takePrefill]
  )

  /** "Which branch are these for?" (handoff §2.1 `needs_branch`). */
  const answerPrefillBranch = useCallback(
    (branchId: number | 'all') => {
      if (answeringPrefill) return
      setAnsweringPrefill(true)
      runPrefill(branchId)
        .then(() => refreshSummary())
        .catch(fail)
        .finally(() => {
          if (mountedRef.current) setAnsweringPrefill(false)
        })
    },
    [answeringPrefill, fail, refreshSummary, runPrefill]
  )

  const takeEdit = useCallback((result: AllocationEditResult) => {
    if (!mountedRef.current) return
    setGrid((prev) => (prev ? mergeServerCells(prev, result.cells) : prev))
    setProgress((prev) => ({...prev, overall: result.overall, canContinue: result.canContinue}))
    callbacksRef.current.onEdited?.()
  }, [])

  /** Teacher pick (§4.1): shown at once, then replaced by what the server saved. */
  const setTeacher = useCallback(
    (subjectId: number, teacherId: number | null) => {
      const current = gridRef.current
      const before = current?.branches
        .flatMap((b) => b.rows)
        .map((row) => Object.values(row.cells).find((cell) => cell.subjectId === subjectId))
        .find(Boolean)
      if (current && before) {
        const teacher = teacherId
          ? (current.teachers.find((t) => t.id === teacherId) ?? null)
          : null
        setGrid((prev) =>
          prev ? mergeServerCells(prev, [withCellTeacher(before, teacher)]) : prev
        )
      }
      return retryBusy(() => api.setTeacher(dispatch, subjectId, teacherId)).then(
        takeEdit,
        (error) => {
          // Undo the pick on screen at once: the reload below needs the network too, and when
          // the connection is down it fails as well, which left the picked teacher in the cell
          // although nothing was saved.
          if (mountedRef.current && before) {
            setGrid((prev) => (prev ? mergeServerCells(prev, [before]) : prev))
          }
          fail(error)
          // Back to what the server holds.
          loadGrid()
        }
      )
    },
    [dispatch, fail, loadGrid, takeEdit]
  )

  /** Cell settings "Save changes" (§4.2). Rejects on a refusal so the panel stays open. */
  const saveSettings = useCallback(
    (
      subjectId: number,
      body: {weeklyPeriods?: number; dailyPeriods?: number; consecutivePeriods?: number}
    ) =>
      retryBusy(() => api.setSettings(dispatch, subjectId, body)).then(takeEdit, (error) => {
        fail(error)
        return Promise.reject(error)
      }),
    [dispatch, fail, takeEdit]
  )

  /** Not taught (§4.3); resolves with the batch that undoes it. */
  const markNotTaught = useCallback(
    (subjectId: number) =>
      retryBusy(() => api.markNotTaught(dispatch, subjectId)).then(
        (res) => {
          takeEdit(res)
          return res.batchId
        },
        (error) => {
          fail(error)
          return Promise.reject(error)
        }
      ),
    [dispatch, fail, takeEdit]
  )

  /**
   * Restore (§4.4): the cell is required again, with the teacher it had when it was marked not
   * taught if the server brings that teacher back. Resolves with the batch that undoes it and the
   * restored cell, so the confirmation can say whether a teacher came back.
   */
  const restore = useCallback(
    (subjectId: number) =>
      retryBusy(() => api.restore(dispatch, subjectId)).then(
        (res) => {
          takeEdit(res)
          return {
            batchId: res.batchId,
            cell: res.cells.find((cell) => cell.subjectId === subjectId) ?? null,
          }
        },
        (error) => {
          fail(error)
          return Promise.reject(error)
        }
      ),
    [dispatch, fail, takeEdit]
  )

  /** Undo (§4.5). Rows and columns can come back, so the grid is fetched again. */
  const undo = useCallback(
    (batchId: string) =>
      api.undo(dispatch, batchId).then(
        (res) => {
          takeEdit(res)
          refresh()
        },
        (error) => {
          fail(error)
          return Promise.reject(error)
        }
      ),
    [dispatch, fail, refresh, takeEdit]
  )

  const previewRemoval = useCallback(
    (request: AllocationRemovalRequest) =>
      api.previewRemoval(dispatch, request).catch((error) => {
        fail(error)
        return Promise.reject(error)
      }),
    [dispatch, fail]
  )

  /**
   * Removal apply (§5) with the previewed count. `REMOVAL_CHANGED` is left to the caller, which
   * previews again.
   */
  const applyRemoval = useCallback(
    (request: AllocationRemovalRequest, expectedCount: number) =>
      retryBusy(() => api.applyRemoval(dispatch, {...request, expectedCount})).then(
        (res) => {
          if (mountedRef.current) {
            setProgress((prev) => ({...prev, overall: res.overall, canContinue: res.canContinue}))
          }
          callbacksRef.current.onEdited?.()
          refresh()
          return res
        },
        (error: AllocationServerError) => {
          if (error.code !== 'REMOVAL_CHANGED') fail(error)
          return Promise.reject(error)
        }
      ),
    [dispatch, fail, refresh]
  )

  /** Progress after a change made elsewhere (an applied Versi proposal). */
  const takeProgress = useCallback((overall: AllocationProgress, canContinue: boolean) => {
    setProgress((prev) => ({...prev, overall, canContinue}))
  }, [])

  const view = useMemo(() => buildServerGridView(grid), [grid])

  return {
    status,
    overall: progress.overall,
    canContinue: progress.canContinue,
    prefilledCount: progress.prefilledCount,
    prefill,
    answeringPrefill,
    answerPrefillBranch,
    answerPrefillQuestions,
    grid,
    gridLoading,
    summaryLoading,
    preview,
    view,
    loadGrid,
    refresh,
    refreshSummary,
    setTeacher,
    saveSettings,
    markNotTaught,
    restore,
    undo,
    previewRemoval,
    applyRemoval,
    takeProgress,
  }
}

export type AllocationServer = ReturnType<typeof useAllocationServer>
